import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Icon } from '../design-system/icons';
import { supabase } from '../lib/supabase';
import { cerrarSesion } from '../lib/cerrarSesion';
import { trackEvent } from '../lib/posthog';
import ContactarPorWhatsApp from '../components/ContactarPorWhatsApp';
import {
  mensajeErrorVerificacion,
  normalizarCelular,
  PAISES_TELEFONO,
  type PaisTelefono,
} from '../lib/telefonoVerificacion';

/**
 * Verificación de celular por SMS para cuentas nuevas (anti-abuso del trial).
 * Ruta PÚBLICA con guard de sesión propio (patrón AccountSetup): acá llega el
 * redirect bloqueante de checkUserProfile (App.tsx). El OTP lo genera y valida
 * Supabase Auth (updateUser + verifyOtp type phone_change); el SMS sale por el
 * Send SMS Hook → atlas-backend → Brevo. La unicidad (1 celular = 1 cuenta) la
 * garantiza Supabase (error phone_exists).
 * Ver ADR-2026-08-31-verificacion-celular-supabase.
 */
export const VerificarCelular = () => {
  const navigate = useNavigate();
  const [paso, setPaso] = useState<'telefono' | 'codigo'>('telefono');
  const [pais, setPais] = useState<PaisTelefono>(PAISES_TELEFONO[0]);
  const [paisAbierto, setPaisAbierto] = useState(false);
  const [telefono, setTelefono] = useState('');
  const [telefonoE164, setTelefonoE164] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // El link de soporte recién aparece cuando hay señales de que el SMS no llega:
  // 2+ envíos (pidió reenviar) o 2+ códigos fallidos. Antes de eso, no distrae.
  const [envios, setEnvios] = useState(0);
  const [fallosCodigo, setFallosCodigo] = useState(0);
  const saliendo = useRef(false);
  const mostrarAyuda = envios >= 2 || fallosCodigo >= 2;

  // Necesita sesión (viene post-login). Sin sesión → /auth. Si el celular ya está
  // verificado (entró a mano), no hay nada que hacer acá.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (!data.session) {
        navigate('/auth', { replace: true });
        return;
      }
      if (data.session.user.phone_confirmed_at) {
        window.location.replace('/');
      }
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  // Ticker del cooldown de reenvío (espejo del throttle de 60s de Supabase).
  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const enviarCodigo = async () => {
    if (loading) return;
    const e164 = normalizarCelular(telefono, pais.iso);
    if (!e164) {
      setError('El número no parece válido. Revisalo.');
      return;
    }
    setError(null);
    setAviso(null);
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ phone: e164 });
    setLoading(false);
    if (err) {
      setError(mensajeErrorVerificacion(err));
      // Cast puntual: AuthError.code no existe en los tipos de supabase-js viejos.
      trackEvent('phone_gate_error', {
        paso: 'enviar',
        code: (err as { code?: string }).code ?? String(err.status ?? ''),
      });
      return;
    }
    setTelefonoE164(e164);
    setPaso('codigo');
    setCooldown(60);
    setEnvios((n) => n + 1);
    trackEvent('phone_gate_sms_enviado');
  };

  const verificarCodigo = async () => {
    if (loading || codigo.trim().length < 6) return;
    setError(null);
    setAviso(null);
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({
      phone: telefonoE164,
      token: codigo.trim(),
      type: 'phone_change',
    });
    setLoading(false);
    if (err) {
      setError(mensajeErrorVerificacion(err));
      setFallosCodigo((n) => n + 1);
      trackEvent('phone_gate_error', {
        paso: 'verificar',
        code: (err as { code?: string }).code ?? String(err.status ?? ''),
      });
      return;
    }
    trackEvent('phone_gate_verificado');
    // Recarga completa: checkUserProfile re-corre con la sesión ya actualizada
    // (phone_confirmed_at seteado) y sigue el flujo normal (wizard u home).
    window.location.replace('/');
  };

  const reenviar = async () => {
    if (cooldown > 0 || loading) return;
    setError(null);
    setAviso(null);
    // OJO: resend({ type: 'phone_change' }) NO sirve acá — GoTrue lo rechaza con
    // "Phone logins are disabled" porque el login por teléfono está deshabilitado a
    // propósito (solo verificamos). updateUser({ phone }) regenera el OTP y re-dispara
    // el Send SMS Hook igual, con el provider así (verificado en logs de auth 2026-09-01).
    const { error: err } = await supabase.auth.updateUser({ phone: telefonoE164 });
    if (err) {
      setError(mensajeErrorVerificacion(err));
      return;
    }
    setCodigo('');
    setAviso('Listo, te enviamos un código nuevo.');
    setCooldown(60);
    setEnvios((n) => n + 1);
    trackEvent('phone_gate_sms_reenviado');
  };

  const salir = async () => {
    if (saliendo.current) return;
    saliendo.current = true;
    await cerrarSesion('/auth');
  };

  // Flecha de volver (mismo criterio que el wizard de onboarding): en el paso del
  // código retrocede al teléfono; en el paso del teléfono no hay paso anterior, así
  // que salir es desloguearse (ADR-2026-08-03-volver-en-el-paso-1-desloguea).
  const volver = () => {
    if (paso === 'codigo') {
      setPaso('telefono');
      setCodigo('');
      setError(null);
      return;
    }
    void salir();
  };

  return (
    <div className="flex min-h-[100svh] justify-center bg-white font-sans">
      <div className="flex w-full max-w-[480px] flex-col px-5 pb-8 pt-[calc(env(safe-area-inset-top)+20px)] md:px-8 md:pt-8">
        {/* Flecha de volver, calcada del header del wizard (SetupProgress). */}
        <button
          type="button"
          onClick={volver}
          aria-label="Volver"
          className="-m-1 self-start rounded-full p-1 text-[#18181B] transition-colors hover:bg-[#F4F4F5]"
        >
          <Icon name="outline/move-left" size={24} />
        </button>
        <h1 className="mt-6 text-[24px] font-[700] leading-[29px] text-[#18181B]">
          Verificá tu celular
        </h1>
        <p className="mt-2 text-[15px] leading-[21px] text-[#52525C]">
          {paso === 'telefono'
            ? 'Te mandamos un código por SMS para confirmar que sos vos. Lo pedimos una sola vez.'
            : `Te enviamos un código de 6 dígitos por SMS al ${telefonoE164}.`}
        </p>

        {paso === 'telefono' ? (
          <div className="mt-8 flex flex-col gap-3">
            <div className="flex gap-2">
              {/* Selector de país (prefijo). Custom como OnboardingSelects: sin dropdown nativo. */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPaisAbierto((v) => !v)}
                  className="flex h-full items-center gap-1 rounded-[12px] border border-[#E4E4E7] px-3 text-[15px] font-[500] text-[#18181B]"
                >
                  {pais.prefijo}
                  <ChevronDown className="size-4 text-[#52525C]" />
                </button>
                {paisAbierto && (
                  <div className="absolute left-0 top-[calc(100%+4px)] z-20 max-h-64 w-56 overflow-y-auto rounded-[12px] border border-[#E4E4E7] bg-white py-1 shadow-lg">
                    {PAISES_TELEFONO.map((p) => (
                      <button
                        key={p.iso}
                        type="button"
                        onClick={() => {
                          setPais(p);
                          setPaisAbierto(false);
                        }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-[14px] text-[#18181B] hover:bg-[#F4F4F5]"
                      >
                        <span>{p.nombre}</span>
                        <span className="text-[#52525C]">{p.prefijo}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void enviarCodigo()}
                placeholder={pais.iso === 'AR' ? '11 5555-1234' : 'Tu número'}
                className="w-full rounded-[12px] border border-[#E4E4E7] px-4 py-3 text-[16px] text-[#18181B] outline-none focus:border-[#18181B]"
              />
            </div>
            {pais.iso === 'AR' && (
              <p className="text-[13px] leading-[18px] text-[#71717B]">
                Código de área + número, sin el 15. Ej.: 11 5555-1234.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-3">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && void verificarCodigo()}
              placeholder="000000"
              className="w-full rounded-[12px] border border-[#E4E4E7] px-4 py-3 text-center text-[24px] tracking-[8px] text-[#18181B] outline-none focus:border-[#18181B]"
            />
            <button
              type="button"
              onClick={() => void reenviar()}
              disabled={cooldown > 0}
              className="self-start text-[14px] font-[600] text-[#18181B] underline disabled:cursor-not-allowed disabled:text-[#9F9FA9] disabled:no-underline"
            >
              {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
            </button>
            <button
              type="button"
              onClick={() => {
                setPaso('telefono');
                setCodigo('');
                setError(null);
              }}
              className="self-start text-[14px] text-[#52525C] underline"
            >
              Cambiar el número
            </button>
          </div>
        )}

        {error && (
          <p className="mt-4 text-[14px] leading-[19px] text-[#DC2626]" role="alert">
            {error}
          </p>
        )}
        {aviso && !error && (
          <p className="mt-4 text-[14px] leading-[19px] text-[#16A34A]" role="status">
            {aviso}
          </p>
        )}

        <div className="mt-8">
          <button
            type="button"
            onClick={() => void (paso === 'telefono' ? enviarCodigo() : verificarCodigo())}
            disabled={loading || (paso === 'telefono' ? !telefono.trim() : codigo.trim().length < 6)}
            className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] disabled:cursor-not-allowed disabled:bg-[#9F9FA9]"
          >
            {loading && <Loader2 className="size-4 animate-spin" />}
            {paso === 'telefono' ? 'Enviarme el código' : 'Verificar'}
          </button>
        </div>

        {/* Ayuda discreta: solo cuando hay señales de problema (ver mostrarAyuda). */}
        {mostrarAyuda && (
          <div className="mt-auto flex flex-col items-center pt-10">
            <ContactarPorWhatsApp
              mensaje="Buenas, no me llega el SMS para verificar mi celular en Atlas."
              tituloQR="Te ayudamos a verificar"
              variante="texto"
              className="text-[14px] text-[#2563EB]"
            >
              ¿No te llega el SMS? Escribinos
            </ContactarPorWhatsApp>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificarCelular;
